import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  ImportFailureDto,
  QueueLogoImportResponse,
} from "../models/responses/ImportFailureDto";

export interface ImportFailuresRepository {
  /**
   * Currently-failing import item numbers (GET admin/import-failures).
   * Polled: same house pattern as the import-queue counts (#598) — 60s
   * refetchInterval, paused while the tab is unfocused, plus the screen's
   * manual refresh button.
   */
  useImportFailures(): Resource<ImportFailureDto[]>;
  /**
   * Re-queues one item number on the matching import queue
   * (POST logo-import/queue / design-import/queue) and invalidates the
   * failure list. The row does NOT disappear immediately — the queue handler
   * deletes it only when the import actually succeeds.
   */
  requeueImport(
    itemNo: string,
    kind: string,
  ): Promise<QueueLogoImportResponse | void>;
}

const IMPORT_FAILURES_KEY = ["import-failures"];

export function useImportFailuresRepository(): ImportFailuresRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useImportFailures(): Resource<ImportFailureDto[]> {
    return useQuery(IMPORT_FAILURES_KEY, () => apiClient.getImportFailures(), {
      refetchInterval: 60000,
      staleTime: 55000,
    });
  }

  async function requeueImport(
    itemNo: string,
    kind: string,
  ): Promise<QueueLogoImportResponse | void> {
    const result =
      kind === "design"
        ? await apiClient.queueDesignImportByItemNo(itemNo)
        : await apiClient.queueLogoImportByItemNo(itemNo);
    // Background invalidation: attempts/lastSeen may move as the queue handler
    // processes the message, and a successful import deletes the row.
    await queryClient.invalidateQueries({ queryKey: IMPORT_FAILURES_KEY });
    return result;
  }

  return {
    useImportFailures,
    requeueImport,
  };
}
